import { Autocomplete, Avatar, Box, Button, DataTable, DataTableStatusCell, FormField, Typography, IPropDetailContainer } from "@maysoft/common-component-react";
import { Card, Grid } from "@mui/material";

import Helpers from "../../commons/helpers";
import Strings from "../../constants/strings";

import { useMemo } from "react";
import { Constants } from "../../constants/constants";
import { ICodename, ITopic } from "../../commons/interfaces";
import { ITopicError } from "../../hooks/topic/edit/useData";
import { Mode, SubscribeStatus } from "../../constants/enums";
import { useData, useSelectData, useSubmit, useUserTopic } from "../../hooks/topic/edit";



const TopicEditContainer = ({
    mode,
    idDetail,
    clientId,
    organizationId,
    onGoBack,
    onChangeMode,
}: IPropDetailContainer & {
    clientId?: string;
    organizationId?: string;
}) => {
    const acUpdate = useMemo(() => !Helpers.isNullOrEmpty(idDetail), [idDetail]);

    const { categoryData, typeData } = useSelectData({ organizationId });
    const { data, error, setError, onChangeValue } = useData({ id: idDetail });
    const { onSubmit } = useSubmit({ data: { ...data, clientId, organizationId }, mode, onGoBack, setError, onChangeMode });

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "end", mb: 2 }}>
                    {Helpers.isFunction(onGoBack) && (
                        <Button color={"secondary"} onClick={onGoBack}>
                            {Strings.Common.GO_BACK}
                        </Button>
                    )}
                    {acUpdate && (
                        <Button sx={{ ml: 1 }} color={"dark"} onClick={onSubmit}>
                            {mode === Mode.View ? Strings.Common.EDIT : Strings.Common.SAVE}
                        </Button>
                    )}
                    {!acUpdate && mode === Mode.Create && (
                        <Button sx={{ ml: 1 }} color={"dark"} onClick={onSubmit}>
                            {Strings.Common.SAVE}
                        </Button>
                    )}
                </Box>
            </Grid>
            <Grid item xs={12}>
                <BasicInfo mode={mode} categoryData={categoryData} typeData={typeData} data={data} error={error} onChangeValue={onChangeValue} />
            </Grid>
            {mode !== Mode.Create && (
                <Grid item xs={12}>
                    <TopicListContainer mode={mode} topicId={data?.id} organizationId={organizationId} />
                </Grid>
            )}
        </Grid>
    );
};

const TopicListContainer = ({ mode, topicId, organizationId }: { mode: Mode; topicId: string; organizationId?: string }) => {
    const language = Strings.getLanguage();
    const { rows, model, getPaged, handleBlock, onChangeValue, onResetModel } = useUserTopic({
        topicId,
        organizationId,
    });

    const FilterForm = () => (
        <Box>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FormField
                        maxLength={255}
                        label={Strings.TOPIC.SEARCH_TEXT}
                        placeholder={Strings.TOPIC.SEARCH_TEXT}
                        defaultValue={model.requestDataTemp?.searchText || ""}
                        onBlur={(value) => onChangeValue(value, "searchText")}
                    />
                </Grid>
            </Grid>
        </Box>
    );

    const getStatusCellData = (type: SubscribeStatus) => {
        if (type === SubscribeStatus.Block)
            return {
                icon: "block",
                color: "warning",
                title: Strings.TOPIC.BLOCKED,
            };
        if (type === SubscribeStatus.Subscribed)
            return {
                icon: "check",
                color: "success",
                title: Strings.TOPIC.SUBSCRIBED,
            };
        if (type === SubscribeStatus.Unsubscribed)
            return {
                icon: "close",
                color: "secondary",
                title: Strings.TOPIC.UNSUBSCRIBED,
            };
    };

    const columns = useMemo(
        () => [
            {
                Header: Strings.TOPIC.AVATAR,
                accessor: "avatarUrl",
                width: "100px",
                Cell: (row: any) => <Avatar text={row.row?.original?.userName} src={row.value} />,
            },
            {
                Header: Strings.TOPIC.USER_NAME,
                accessor: "userName",
                width: "100px",
            },
            {
                Header: Strings.TOPIC.STATUS,
                accessor: "subscribeStatus",
                Cell: ({ value }: any) => <DataTableStatusCell data={getStatusCellData(value) as any} />,
            },
        ],
        [language]
    );

    return (
        <Card>
            <Box p={2}>
                <Box pb={1}>
                    <Typography variant="h5">{Strings.TOPIC.LIST_OF_USER}</Typography>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card>
                            <Box p={2}>
                                <DataTable
                                    mode={mode}
                                    totalCount={model.requestData?.totalCount || 0}
                                    pageNumber={model.requestData?.pageNumber || 1}
                                    rowPerPage={model.requestData?.pageSize || Constants.ROW_PER_PAGE}
                                    onChangePageSize={(pageSize) => getPaged({ ...model.requestData, pageSize })}
                                    onChangePageNumber={(pageNumber) => getPaged({ ...model.requestData, pageNumber })}
                                    //
                                    searchText={model.requestData?.searchText}
                                    placeholderSearch={Strings.TOPIC.SEARCH_TEXT}
                                    onSearch={(val) => getPaged({ ...model.requestData, pageNumber: 1, searchText: val })}
                                    onSearchChange={(value) => onChangeValue(value, "searchText")}
                                    filterForm={<FilterForm />}
                                    onFilter={() => getPaged({ ...model.requestDataTemp, pageNumber: 1 })}
                                    onReset={() =>
                                        getPaged({
                                            ...model.requestData,
                                            pageNumber: 1,
                                            searchText: "",
                                        })
                                    }
                                    onCloseFilter={onResetModel}
                                    //
                                    table={{
                                        columns,
                                        rows,
                                    }}
                                    actionList={(row: any) => [
                                        row.subscribeStatus !== SubscribeStatus.Block && {
                                            key: "lock",
                                            iconName: "lock",
                                            title: Strings.Common.BLOCK,
                                            callback: (row: any) => handleBlock(row),
                                        },
                                    ]}
                                />
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Card>
    );
};

const BasicInfo = ({
    mode,
    data,
    categoryData,
    typeData,
    error,
    onChangeValue,
}: {
    mode: Mode;
    data: ITopic;
    categoryData: ICodename[];
    typeData: ICodename[];
    error: ITopicError;
    onChangeValue: (value: string, key: keyof ITopic) => void;
}) => {
    return (
        <Card>
            <Box p={2}>
                <Box pb={1}>
                    <Typography variant="h5">{Strings.TOPIC.BASIC_INFO}</Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            // required
                            isSelectedBox
                            mode={mode}
                            data={categoryData || []}
                            defaultValue={data?.categoryId}
                            errorMessage={error?.categoryId}
                            label={Strings.TOPIC.CATEGORY}
                            placeholder={Strings.TOPIC.SELECT_CATEGORY}
                            onChange={(value) => onChangeValue(value, "categoryId")}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            required
                            isSelectedBox
                            mode={mode}
                            data={typeData || []}
                            defaultValue={data?.type}
                            errorMessage={error?.type}
                            label={Strings.TOPIC.TYPE}
                            placeholder={Strings.TOPIC.SELECT_TYPE}
                            onChange={(value) => onChangeValue(value, "type")}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormField
                            required
                            mode={mode}
                            maxLength={255}
                            label={Strings.TOPIC.NAME}
                            placeholder={Strings.TOPIC.INPUT_NAME}
                            disabled={mode === Mode.Update}
                            defaultValue={data?.topicName}
                            errorMessage={error?.topicName}
                            onBlur={(value) => onChangeValue(value, "topicName")}
                            onFocus={(e: any) => (e.target.value = Helpers.checkMaxLengthTypeNumber(e.target.value, 255))}
                        />
                        {mode === Mode.Create && (
                            <Typography sx={{ mt: 0.5, fontSize: "0.8rem", fontWeight: "bold" }}>{Strings.TOPIC.NAME_ALLOWED_CHARS}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <FormField
                            required
                            mode={mode}
                            multiline
                            label={Strings.TOPIC.DESCRIPTION}
                            placeholder={Strings.TOPIC.INPUT_DESCRIPTION}
                            defaultValue={data?.description}
                            errorMessage={error?.description}
                            onBlur={(value) => onChangeValue(value, "description")}
                            onFocus={(e: any) => (e.target.value = Helpers.checkMaxLengthTypeNumber(e.target.value, 255))}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Card>
    );
};
export default TopicEditContainer;
